import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Trophy, Flame, Award, Zap, TrendingUp, Users, Loader2, Plus,
  Brain, FileText, Trash2, PenLine, Sparkles, CheckCircle, Eye
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { value: 'mcq', label: 'Multiple Choice' },
  { value: 'true_false', label: 'True / False' },
  { value: 'essay', label: 'Essay' },
  { value: 'short_answer', label: 'Short Answer' },
  { value: 'fill_blank', label: 'Fill in the Blank' },
];

const CounselorGamificationView = ({ students: parentStudents }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMode, setActiveMode] = useState('leaderboard');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createMode, setCreateMode] = useState('manual');
  const [createdAssignments, setCreatedAssignments] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [showReportDialog, setShowReportDialog] = useState(null);

  // Manual form
  const [manualForm, setManualForm] = useState({
    title: '', description: '', type: 'wellness', due_date: '', questions: [{ question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '', marks: 1 }]
  });

  // AI form
  const [aiForm, setAiForm] = useState({
    topic: '', type: 'wellness', difficulty: 'medium', num_questions: 5,
    question_types: ['mcq', 'essay'], custom_instructions: '', due_date: '', auto_assign: true
  });
  const [aiGenerated, setAiGenerated] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/counselor/gamification-overview`); setData(res.data); } catch { toast.error('Failed to load gamification data'); }
    finally { setLoading(false); }
  }, []);

  const fetchAssignments = useCallback(async () => {
    try { const res = await axios.get(`${API}/counselor/assignments`); setCreatedAssignments(res.data.assignments || []); } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchData(); fetchAssignments(); }, [fetchData, fetchAssignments]);

  // === MANUAL QUESTION HELPERS ===
  const addQuestion = () => {
    setManualForm(prev => ({ ...prev, questions: [...prev.questions, { question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '', marks: 1 }] }));
  };
  const removeQuestion = (idx) => {
    setManualForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
  };
  const updateQuestion = (idx, field, value) => {
    setManualForm(prev => {
      const q = [...prev.questions];
      q[idx] = { ...q[idx], [field]: value };
      return { ...prev, questions: q };
    });
  };
  const updateOption = (qIdx, oIdx, value) => {
    setManualForm(prev => {
      const q = [...prev.questions];
      const opts = [...q[qIdx].options];
      opts[oIdx] = value;
      q[qIdx] = { ...q[qIdx], options: opts };
      return { ...prev, questions: q };
    });
  };

  // === SUBMIT MANUAL ===
  const handleManualSubmit = async () => {
    if (!manualForm.title || manualForm.questions.some(q => !q.question)) {
      toast.error('Fill in title and all questions'); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/counselor/assignment/create`, {
        title: manualForm.title, description: manualForm.description,
        type: manualForm.type, due_date: manualForm.due_date,
        questions: manualForm.questions, student_ids: selectedStudentIds
      });
      toast.success(res.data.message);
      setShowCreateDialog(false);
      setManualForm({ title: '', description: '', type: 'wellness', due_date: '', questions: [{ question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '', marks: 1 }] });
      fetchAssignments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSubmitting(false); }
  };

  // === AI GENERATE ===
  const handleAiGenerate = async () => {
    if (!aiForm.topic) { toast.error('Please enter a topic'); return; }
    setGenerating(true); setAiGenerated(null);
    try {
      const res = await axios.post(`${API}/counselor/assignment/ai-generate`, {
        topic: aiForm.topic, type: aiForm.type, difficulty: aiForm.difficulty,
        num_questions: aiForm.num_questions, question_types: aiForm.question_types,
        custom_instructions: aiForm.custom_instructions,
        due_date: aiForm.due_date, auto_assign: false, student_ids: []
      });
      setAiGenerated(res.data.generated);
      toast.success('Questions generated! Review and assign below.');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI generation failed'); }
    finally { setGenerating(false); }
  };

  // === ASSIGN AI GENERATED ===
  const handleAssignAiGenerated = async () => {
    if (!aiGenerated) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/counselor/assignment/create`, {
        title: aiGenerated.title, description: aiGenerated.description,
        type: aiForm.type, due_date: aiForm.due_date,
        questions: aiGenerated.questions, student_ids: selectedStudentIds
      });
      toast.success(res.data.message);
      setShowCreateDialog(false); setAiGenerated(null);
      setAiForm({ topic: '', type: 'wellness', difficulty: 'medium', num_questions: 5, question_types: ['mcq', 'essay'], custom_instructions: '', due_date: '', auto_assign: true });
      fetchAssignments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-12" data-testid="gamification-loading"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;

  const topXP = data?.students?.[0]?.xp || 1;
  const totalXP = data?.students?.reduce((s, st) => s + st.xp, 0) || 0;
  const avgXP = data?.students?.length ? Math.round(totalXP / data.students.length) : 0;
  const activeStreaks = data?.students?.filter(s => s.streak_days > 0).length || 0;

  return (
    <div className="space-y-6" data-testid="counselor-gamification-view">
      <Tabs value={activeMode} onValueChange={setActiveMode}>
        <div className="flex justify-between items-center mb-4">
          <TabsList>
            <TabsTrigger value="leaderboard" data-testid="gam-leaderboard-tab"><Trophy className="w-4 h-4 mr-1" /> Leaderboard</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="gam-assignments-tab"><FileText className="w-4 h-4 mr-1" /> Quizzes & Assignments</TabsTrigger>
          </TabsList>
          {activeMode === 'assignments' && (
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowCreateDialog(true)} data-testid="create-assignment-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Quiz / Assignment
            </Button>
          )}
        </div>

        {/* LEADERBOARD TAB */}
        <TabsContent value="leaderboard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4 bg-indigo-50 border-indigo-200"><div className="flex items-center gap-3"><Trophy className="w-5 h-5 text-indigo-600" /><div><p className="text-2xl font-bold text-indigo-700" data-testid="total-students-gam">{data?.total || 0}</p><p className="text-xs text-indigo-600">Students Tracked</p></div></div></Card>
            <Card className="p-4 bg-amber-50 border-amber-200"><div className="flex items-center gap-3"><Zap className="w-5 h-5 text-amber-600" /><div><p className="text-2xl font-bold text-amber-700">{totalXP.toLocaleString()}</p><p className="text-xs text-amber-600">Total XP Earned</p></div></div></Card>
            <Card className="p-4 bg-green-50 border-green-200"><div className="flex items-center gap-3"><TrendingUp className="w-5 h-5 text-green-600" /><div><p className="text-2xl font-bold text-green-700">{avgXP.toLocaleString()}</p><p className="text-xs text-green-600">Average XP</p></div></div></Card>
            <Card className="p-4 bg-orange-50 border-orange-200"><div className="flex items-center gap-3"><Flame className="w-5 h-5 text-orange-600" /><div><p className="text-2xl font-bold text-orange-700">{activeStreaks}</p><p className="text-xs text-orange-600">Active Streaks</p></div></div></Card>
          </div>

          {(!data || data.students.length === 0) ? (
            <Card className="p-12 text-center"><Users className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No student gamification data available yet</p></Card>
          ) : (
            <>
              <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Student Engagement Leaderboard</h3>
                <div className="space-y-3">
                  {data.students.map((student, idx) => {
                    const xpPct = topXP > 0 ? (student.xp / topXP) * 100 : 0;
                    const moodEmoji = student.recent_mood === 'great' ? '\u{1F60A}' : student.recent_mood === 'good' ? '\u{1F642}' : student.recent_mood === 'okay' ? '\u{1F610}' : student.recent_mood === 'low' ? '\u{1F614}' : student.recent_mood === 'stressed' ? '\u{1F630}' : '';
                    return (
                      <div key={student.student_id} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors" data-testid={`student-gam-${idx}`}>
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${idx < 3 ? 'bg-amber-400 text-white' : 'bg-gray-200 text-gray-600'}`}>{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1"><span className="font-medium text-sm truncate">{student.name}</span>{moodEmoji && <span className="text-lg">{moodEmoji}</span>}<Badge variant="outline" className="text-xs">{student.grade}</Badge></div>
                          <div className="flex items-center gap-2"><Progress value={xpPct} className="h-1.5 flex-1" /><span className="text-xs text-gray-500 w-16 text-right">{student.xp.toLocaleString()} XP</span></div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 flex-shrink-0">
                          <div className="text-center"><p className="font-semibold text-sm text-indigo-600">Lv.{student.level.level}</p><p className="text-xs">{student.level.name}</p></div>
                          {student.streak_days > 0 && <Badge className="bg-orange-500 text-white text-xs"><Flame className="w-3 h-3 mr-0.5" />{student.streak_days}d</Badge>}
                          <div className="flex items-center gap-1"><Award className="w-3 h-3 text-amber-400" /><span>{student.badges_count}</span></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
              <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <h3 className="font-semibold text-lg mb-4">Activity Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.students.map(s => (
                    <div key={s.student_id} className="p-3 border rounded-lg"><p className="font-medium text-sm mb-2">{s.name}</p><div className="grid grid-cols-2 gap-2 text-xs"><div className="bg-blue-50 rounded p-2 text-center"><p className="font-bold text-blue-700">{s.total_quizzes}</p><p className="text-blue-500">Quizzes</p></div><div className="bg-green-50 rounded p-2 text-center"><p className="font-bold text-green-700">{s.total_assignments}</p><p className="text-green-500">Assignments</p></div></div></div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        {/* QUIZZES & ASSIGNMENTS TAB */}
        <TabsContent value="assignments" className="space-y-6">
          {createdAssignments.length === 0 ? (
            <Card className="p-12 text-center"><FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500 mb-4">No quizzes or assignments created yet</p><Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowCreateDialog(true)} data-testid="create-first-assignment-btn"><Plus className="w-4 h-4 mr-2" /> Create Your First Quiz</Button></Card>
          ) : (
            <div className="grid gap-4">
              {createdAssignments.map(a => (
                <Card key={a.id} className="p-5 bg-white/90 backdrop-blur-sm border-0 shadow-lg" data-testid={`assignment-${a.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{a.title}</h3>
                        <Badge variant="outline" className="capitalize">{a.type}</Badge>
                        <Badge className="bg-green-100 text-green-700">{a.question_paper?.length || 0} Q</Badge>
                        {a.submissions_count > 0 && <Badge className="bg-blue-100 text-blue-700">{a.submissions_count}/{a.assigned_count} submitted</Badge>}
                      </div>
                      {a.description && <p className="text-sm text-gray-600 mb-2 line-clamp-2">{a.description}</p>}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Total: {a.total_marks} marks</span>
                        <span>Assigned to: {a.assigned_count || a.assigned_to?.length || 0} students</span>
                        {a.due_date && <span>Due: {new Date(a.due_date).toLocaleDateString()}</span>}
                        <span>Created: {new Date(a.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {a.submissions_count > 0 && (
                      <Button size="sm" variant="outline" onClick={() => setShowReportDialog(a)} data-testid={`view-subs-${a.id}`}>
                        <Eye className="w-4 h-4 mr-1" /> Reports
                      </Button>
                    )}
                  </div>
                  {/* Inline submissions preview */}
                  {a.submissions?.length > 0 && (
                    <div className="mt-3 pt-3 border-t space-y-2">
                      {a.submissions.map(sub => (
                        <div key={sub.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{sub.student_name || 'Student'}</span>
                            <Badge className={`text-xs ${(sub.score_percentage || 0) >= 70 ? 'bg-green-100 text-green-700' : (sub.score_percentage || 0) >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{sub.score_percentage || 0}%</Badge>
                            {sub.ai_evaluation && <Badge className="bg-purple-100 text-purple-700 text-xs"><Brain className="w-3 h-3 mr-0.5" />AI Report</Badge>}
                          </div>
                          <span className="text-xs text-gray-400">{new Date(sub.submitted_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* CREATE QUIZ/ASSIGNMENT DIALOG */}
      <Dialog open={showCreateDialog} onOpenChange={(v) => { setShowCreateDialog(v); if (!v) setAiGenerated(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Quiz / Assignment</DialogTitle></DialogHeader>

          {/* Student Selection */}
          {parentStudents?.length > 0 && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border" data-testid="student-selection">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Assign To Students</Label>
                <Button size="sm" variant="ghost" className="text-xs h-6" onClick={() => setSelectedStudentIds(selectedStudentIds.length === parentStudents.length ? [] : parentStudents.map(s => s.id))}>
                  {selectedStudentIds.length === parentStudents.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {parentStudents.map(s => (
                  <label key={s.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition-all ${selectedStudentIds.includes(s.id) ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`select-student-${s.id}`}>
                    <Checkbox checked={selectedStudentIds.includes(s.id)} onCheckedChange={(v) => setSelectedStudentIds(v ? [...selectedStudentIds, s.id] : selectedStudentIds.filter(id => id !== s.id))} />
                    {s.name}
                  </label>
                ))}
              </div>
              {selectedStudentIds.length === 0 && <p className="text-xs text-gray-400 mt-1">No selection = assign to all students</p>}
            </div>
          )}

          <Tabs value={createMode} onValueChange={setCreateMode} className="mt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="manual" data-testid="manual-mode-tab"><PenLine className="w-4 h-4 mr-1" /> Manual</TabsTrigger>
              <TabsTrigger value="ai" data-testid="ai-mode-tab"><Sparkles className="w-4 h-4 mr-1" /> AI Generate</TabsTrigger>
            </TabsList>

            {/* MANUAL MODE */}
            <TabsContent value="manual" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Title *</Label><Input value={manualForm.title} onChange={e => setManualForm({...manualForm, title: e.target.value})} placeholder="e.g. Stress Management Quiz" data-testid="manual-title" /></div>
                <div><Label>Type</Label><Select value={manualForm.type} onValueChange={v => setManualForm({...manualForm, type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="wellness">Wellness</SelectItem><SelectItem value="behavioral">Behavioral</SelectItem><SelectItem value="academic">Academic</SelectItem><SelectItem value="mixed">Mixed</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label>Description</Label><Textarea value={manualForm.description} onChange={e => setManualForm({...manualForm, description: e.target.value})} placeholder="Brief description..." rows={2} /></div>
              <div><Label>Due Date</Label><Input type="date" value={manualForm.due_date} onChange={e => setManualForm({...manualForm, due_date: e.target.value})} /></div>

              <div className="space-y-4">
                <div className="flex justify-between items-center"><Label className="text-base font-semibold">Questions</Label><Button size="sm" variant="outline" onClick={addQuestion} data-testid="add-question-btn"><Plus className="w-4 h-4 mr-1" /> Add Question</Button></div>
                {manualForm.questions.map((q, qi) => (
                  <Card key={qi} className="p-4 border" data-testid={`manual-question-${qi}`}>
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-semibold text-gray-600">Q{qi + 1}</span>
                      {manualForm.questions.length > 1 && <Button size="sm" variant="ghost" className="text-red-500 h-6" onClick={() => removeQuestion(qi)}><Trash2 className="w-3 h-3" /></Button>}
                    </div>
                    <div className="space-y-3">
                      <Textarea value={q.question} onChange={e => updateQuestion(qi, 'question', e.target.value)} placeholder="Enter question..." rows={2} data-testid={`q-text-${qi}`} />
                      <div className="grid grid-cols-3 gap-3">
                        <div><Label className="text-xs">Type</Label><Select value={q.type} onValueChange={v => updateQuestion(qi, 'type', v)}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{QUESTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
                        <div><Label className="text-xs">Marks</Label><Input type="number" className="h-8 text-xs" value={q.marks} onChange={e => updateQuestion(qi, 'marks', parseInt(e.target.value) || 1)} min={1} /></div>
                        <div><Label className="text-xs">Correct Answer</Label><Input className="h-8 text-xs" value={q.correct_answer} onChange={e => updateQuestion(qi, 'correct_answer', e.target.value)} placeholder="Answer" /></div>
                      </div>
                      {(q.type === 'mcq' || q.type === 'true_false') && (
                        <div><Label className="text-xs">Options</Label><div className="grid grid-cols-2 gap-2">
                          {(q.type === 'true_false' ? ['True', 'False'] : q.options).map((opt, oi) => (
                            <Input key={oi} className="h-8 text-xs" value={q.type === 'true_false' ? opt : (q.options[oi] || '')} onChange={e => q.type !== 'true_false' && updateOption(qi, oi, e.target.value)} placeholder={`Option ${oi + 1}`} readOnly={q.type === 'true_false'} />
                          ))}
                        </div></div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={handleManualSubmit} disabled={submitting} data-testid="submit-manual-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                {submitting ? 'Creating...' : 'Create & Assign to All Students'}
              </Button>
            </TabsContent>

            {/* AI MODE */}
            <TabsContent value="ai" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Topic *</Label><Input value={aiForm.topic} onChange={e => setAiForm({...aiForm, topic: e.target.value})} placeholder="e.g. Stress management, Time management" data-testid="ai-topic" /></div>
                <div><Label>Type</Label><Select value={aiForm.type} onValueChange={v => setAiForm({...aiForm, type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="wellness">Wellness</SelectItem><SelectItem value="behavioral">Behavioral</SelectItem><SelectItem value="academic">Academic</SelectItem><SelectItem value="mixed">Mixed</SelectItem></SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Difficulty</Label><Select value={aiForm.difficulty} onValueChange={v => setAiForm({...aiForm, difficulty: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="easy">Easy</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="hard">Hard</SelectItem></SelectContent></Select></div>
                <div><Label>Questions</Label><Input type="number" value={aiForm.num_questions} onChange={e => setAiForm({...aiForm, num_questions: Math.min(Math.max(parseInt(e.target.value) || 1, 1), 20)})} min={1} max={20} data-testid="ai-num-questions" /></div>
                <div><Label>Due Date</Label><Input type="date" value={aiForm.due_date} onChange={e => setAiForm({...aiForm, due_date: e.target.value})} /></div>
              </div>
              <div><Label>Custom Instructions (optional)</Label><Textarea value={aiForm.custom_instructions} onChange={e => setAiForm({...aiForm, custom_instructions: e.target.value})} placeholder="Any specific requirements..." rows={2} data-testid="ai-instructions" /></div>

              <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={handleAiGenerate} disabled={generating} data-testid="ai-generate-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                {generating ? 'Generating with AI...' : 'Generate with AI'}
              </Button>

              {/* AI GENERATED PREVIEW */}
              {aiGenerated && (
                <div className="space-y-3 border-t pt-4" data-testid="ai-generated-preview">
                  <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /><h4 className="font-semibold">{aiGenerated.title}</h4></div>
                  {aiGenerated.description && <p className="text-sm text-gray-600">{aiGenerated.description}</p>}
                  <div className="space-y-2">
                    {aiGenerated.questions?.map((q, i) => (
                      <Card key={i} className="p-3 border" data-testid={`ai-q-${i}`}>
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-bold text-gray-400 mt-1">Q{i + 1}</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{q.question}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs capitalize">{q.type?.replace('_', ' ')}</Badge>
                              <Badge variant="outline" className="text-xs">{q.marks} marks</Badge>
                            </div>
                            {q.options?.length > 0 && (
                              <div className="mt-2 grid grid-cols-2 gap-1">{q.options.map((o, oi) => (
                                <span key={oi} className={`text-xs px-2 py-1 rounded ${o === q.correct_answer ? 'bg-green-100 text-green-700 font-medium' : 'bg-gray-50 text-gray-600'}`}>{o}</span>
                              ))}</div>
                            )}
                            {q.type === 'essay' && q.correct_answer && <p className="text-xs text-gray-400 mt-1 italic">Model: {q.correct_answer.substring(0, 100)}...</p>}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                  <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={handleAssignAiGenerated} disabled={submitting} data-testid="assign-ai-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    {submitting ? 'Assigning...' : 'Assign to All Students'}
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Submission Report Dialog */}
      <Dialog open={!!showReportDialog} onOpenChange={() => setShowReportDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Submission Reports: {showReportDialog?.title}</DialogTitle></DialogHeader>
          {showReportDialog?.submissions?.map(sub => (
            <Card key={sub.id} className="p-4 border mb-3" data-testid={`report-${sub.id}`}>
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">{sub.student_name || 'Student'}</span>
                <Badge className={`${(sub.score_percentage || 0) >= 70 ? 'bg-green-100 text-green-700' : (sub.score_percentage || 0) >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{sub.score_percentage || 0}% — {sub.earned_marks || 0}/{sub.total_marks || 0}</Badge>
              </div>
              {sub.ai_evaluation ? (
                <div className="space-y-2">
                  {/* Per-question scoring with justification */}
                  {sub.ai_evaluation.question_feedback?.length > 0 && (
                    <div className="space-y-2 mb-3">
                      <p className="text-xs font-semibold text-gray-500">Question Scores:</p>
                      {sub.ai_evaluation.question_feedback.map((qf, i) => (
                        <div key={i} className="p-2 bg-gray-50 rounded border text-xs">
                          <div className="flex justify-between"><span className="font-medium">Q{qf.question_id || i + 1}</span><Badge className={`text-xs ${(qf.score / qf.max_marks) >= 0.7 ? 'bg-green-100 text-green-700' : (qf.score / qf.max_marks) >= 0.4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{qf.score}/{qf.max_marks}</Badge></div>
                          <p className="text-gray-700 mt-1">{qf.feedback}</p>
                          {qf.justification && <p className="text-gray-500 italic mt-1 border-l-2 border-gray-300 pl-2">{qf.justification}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                  {sub.ai_evaluation.overall_feedback && <p className="text-sm text-gray-700">{sub.ai_evaluation.overall_feedback}</p>}
                  {sub.ai_evaluation.strengths?.length > 0 && <div><p className="text-xs font-semibold text-green-600">Strengths:</p>{sub.ai_evaluation.strengths.map((s, i) => <p key={i} className="text-xs text-gray-600 ml-2">- {s}</p>)}</div>}
                  {sub.ai_evaluation.areas_for_improvement?.length > 0 && <div><p className="text-xs font-semibold text-amber-600">Improve:</p>{sub.ai_evaluation.areas_for_improvement.map((a, i) => <p key={i} className="text-xs text-gray-600 ml-2">- {a}</p>)}</div>}
                  {sub.ai_evaluation.suggestions?.length > 0 && <div><p className="text-xs font-semibold text-indigo-600">Suggestions:</p>{sub.ai_evaluation.suggestions.map((s, i) => <p key={i} className="text-xs text-gray-600 ml-2">- {s}</p>)}</div>}
                  {sub.ai_evaluation.wellness_insights && <div className="p-2 bg-purple-50 rounded"><p className="text-xs font-semibold text-purple-600">Wellness Insights:</p><p className="text-xs text-purple-800">{sub.ai_evaluation.wellness_insights}</p></div>}
                  {sub.ai_evaluation.recommended_follow_up && <p className="text-xs text-gray-500 italic">Follow-up: {sub.ai_evaluation.recommended_follow_up}</p>}
                </div>
              ) : <p className="text-sm text-gray-400">AI evaluation pending...</p>}
            </Card>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CounselorGamificationView;
